/**
 * Feature Flags for Multi-Job System Enhancement
 *
 * These flags control the rollout of the new multi-job system enhancements.
 * All flags start as false to ensure no impact on existing functionality.
 * Enable flags gradually as each phase is implemented and tested.
 */

export const FEATURE_FLAGS = {
    /**
     * Phase 2: Database Queue System
     * Enables serialized database operations to prevent corruption
     */
    USE_DATABASE_QUEUE: true,

    /**
     * Phase 3: Centralized Job Manager - DISABLED (ROLLBACK)
     * Enables single polling loop and coordinated job management
     * ROLLED BACK: Causing timing issues with job polling
     */
    USE_NEW_JOB_MANAGER: false,

    /**
     * Phase 4: Database Health Monitoring
     * Enables corruption detection and automatic recovery
     */
    USE_HEALTH_MONITOR: true,

    /**
     * Phase 5: Enhanced Job Coordination
     * Enables coordination between undelivered image recovery and active jobs
     */
    USE_JOB_COORDINATION: false,

    /**
     * Development and Debugging
     */
    ENABLE_DEBUG_LOGGING: true,
    ENABLE_PERFORMANCE_MONITORING: false,
    ENABLE_STATE_VALIDATION: false,

    /**
     * MOMM Simplified UI
     * When true, hides the provider dropdown, intent chip, iteration banner,
     * settings panel, and click-to-select iteration. All generation routes
     * use hardcoded cosyflow names from mommHardcodedCosyflows.js.
     * When false, the full wider-scope MOMM UI and routing are active.
     */
    MOMM_SIMPLIFIED_UI: true,

    /**
     * Safety and Recovery
     */
    ENABLE_AUTOMATIC_RECOVERY: true,

    /**
     * Client error telemetry (docs/CLIENT_ERROR_TELEMETRY_PLAN.md) — POST /api/client-errors.
     * Build-time kill switch. The per-user opt-out is the Settings toggle (localStorage
     * `client_telemetry_disabled`), not this flag.
     *
     * OFF (shipped dark) because POST /api/client-errors does not exist in promptus-api yet.
     * With this true, the client would POST to a 404. Nothing breaks — clientTelemetry.js
     * treats 404 as retryable-but-not-the-event's-fault, bounded by the 100-event / 48 KB queue
     * caps and the 72h TTL (LIMITS in clientTelemetry.js) — but the 404s show up in the user's
     * devtools network tab and up to 48 KB sits in their localStorage. A user seeing repeated
     * failed requests to our own API has every reason to worry.
     *
     * Turning it on requires the endpoint to be deployed: flip this to true in the SAME change
     * that deploys POST /api/client-errors. No other file needs to change — the flag is checked
     * (via isTelemetryEnabled) before every write, request and queue read.
     */
    ENABLE_CLIENT_ERROR_TELEMETRY: false,

    /**
     * Gates the `database_corruption_detected` eventBus emit in
     * DatabaseHealthMonitor.handleCorruption. It previously gated a
     * `showCorruptionAlert()` method that was a console.warn STUB, i.e. the flag
     * advertised a user notification that did not exist; the stub is gone and
     * the flag now controls the real signal. The unconditional console.error in
     * handleCorruption is unaffected, so turning this off never costs
     * diagnostics.
     */
    ENABLE_CORRUPTION_ALERTS: true,

    /**
     * Kept FALSE deliberately. `backupDatabase()` is NOT a real backup:
     *  - it covers only `images`, `collection` and `settings` — 3 of the 16
     *    tables in the live schema. It silently omits the other 13, including
     *    `collectedImages` (the
     *    user's entire Collection, and the largest table), `account`,
     *    `cosyflows`, `history`, `mommChats` (all MoMM chat history),
     *    `collections`, `collectionImages`, `outputImages`,
     *    `imageDescriptions`, `defaultModels`, `offlineModels`,
     *    `recommendedSettings` and `reloadImages`.
     *  - it exists only in the tab's JS heap across the deletion, so a tab
     *    crash / OOM between delete and restore loses everything.
     * Turning it on would ship a FALSE safety guarantee: it makes an unsafe
     * operation look safe, which is worse than leaving it off. It is not a
     * substitute for the user-initiated recovery shell in `src/index.js`
     * (Step 1 retry / Step 2 clear cached media / Step 3 full local reset).
     * `recreateDatabase` has also been removed from the AUTOMATIC recovery
     * ladder, so this flag currently has no live consumer.
     */
    ENABLE_BACKUP_ON_CORRUPTION: false,

    /**
     * Prompt lineage ("prompt evolution") Phase 1: the "Branch from here" action,
     * `parent_uid` on the generation payload, and the Derived-from / Children
     * section in View Details. Default ON (web and desktop) — it is additive,
     * sends one extra key the API stores verbatim, and needs no schema change.
     * Turning it off hides the UI and stops stamping parent_uid; rows already
     * written keep their parentId harmlessly.
     */
    PROMPT_LINEAGE: true,

    /**
     * Prompt composition sections: the "⊞ Compose in sections" editor on every
     * prompt / STRING field (Primary + sections + Tail detail, joined with a
     * single space at submit) and the "Sections" button beside the prompt box.
     * Default ON. Off hides the editors; the submit-time join then has nothing
     * to join, so payloads are exactly as before.
     */
    PROMPT_SECTIONS: true
};

/**
 * Helper function to check if a feature is enabled
 * @param {string} flagName - Name of the feature flag
 * @returns {boolean} - Whether the feature is enabled
 */
export const isFeatureEnabled = (flagName) => {
    return FEATURE_FLAGS[flagName] === true;
};

/**
 * Helper function to enable a feature flag
 * @param {string} flagName - Name of the feature flag
 */
export const enableFeature = (flagName) => {
    if (flagName in FEATURE_FLAGS) {
        FEATURE_FLAGS[flagName] = true;
        console.log(`[FEATURE_FLAGS] Enabled: ${flagName}`);
    } else {
        console.warn(`[FEATURE_FLAGS] Unknown flag: ${flagName}`);
    }
};

/**
 * Helper function to disable a feature flag
 * @param {string} flagName - Name of the feature flag
 */
export const disableFeature = (flagName) => {
    if (flagName in FEATURE_FLAGS) {
        FEATURE_FLAGS[flagName] = false;
        console.log(`[FEATURE_FLAGS] Disabled: ${flagName}`);
    } else {
        console.warn(`[FEATURE_FLAGS] Unknown flag: ${flagName}`);
    }
};

/**
 * Get all feature flags status
 * @returns {object} - Object containing all feature flags and their status
 */
export const getAllFeatureFlags = () => {
    return { ...FEATURE_FLAGS };
};

/**
 * Debug helper to log current feature flag status
 */
export const logFeatureFlags = () => {
    console.log('[FEATURE_FLAGS] Current status:', FEATURE_FLAGS);
};

// Development helper - expose to window in development
if (process.env.NODE_ENV === 'development') {
    window.FEATURE_FLAGS = FEATURE_FLAGS;
    window.enableFeature = enableFeature;
    window.disableFeature = disableFeature;
    window.logFeatureFlags = logFeatureFlags;
}